import React, { useState, useEffect } from 'react';
import { updateEnabled, updateDescription } from '../services/featureFlagsApi';
import TenantManager from './TenantManager';
import UserManager from './UserManager';

const FeatureFlagDetails = ({ flag, onUpdate }) => {
  const [enabled, setEnabled] = useState(flag.enabled);
  const [description, setDescription] = useState(flag.description || '');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (flag) {
      const enabledValue = flag.enabled !== undefined ? flag.enabled : flag.Enabled;
      const descriptionValue = flag.description || flag.Description || '';
      setEnabled(enabledValue);
      setDescription(descriptionValue);
      setIsEditingDescription(false);
      setError(null);
    }
  }, [flag?.id || flag?.Id, flag?.enabled !== undefined ? flag.enabled : flag?.Enabled, flag?.description || flag?.Description]);

  const handleToggleEnabled = async () => {
    try {
      setSaving(true);
      setError(null);
      const newEnabled = !enabled;
      const currentFlagId = flag.id || flag.Id;
      if (!currentFlagId) {
        setError('Feature flag ID is missing');
        return;
      }
      await updateEnabled(currentFlagId, newEnabled);
      setEnabled(newEnabled);
      onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to update enabled status');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDescription = async () => {
    try {
      setSaving(true);
      setError(null);
      const currentFlagId = flag.id || flag.Id;
      if (!currentFlagId) {
        setError('Feature flag ID is missing');
        return;
      }
      await updateDescription(currentFlagId, description);
      setIsEditingDescription(false);
      onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to update description');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDescription = () => {
    setDescription(flag.description || flag.Description || '');
    setIsEditingDescription(false);
  };

  const conditions = flag.conditions || flag.Conditions;
  const clientFilters = conditions?.clientFilters || conditions?.ClientFilters || conditions?.client_filters || [];
  const tenantUserAccess = clientFilters.find(
    f => (f.name || f.Name || '').toLowerCase() === 'tenantuseraccess'
  );

  let allowedTenants = [];
  let tenantUserMap = {};

  if (tenantUserAccess) {
    let params = tenantUserAccess.parameters || tenantUserAccess.Parameters;
    if (!params || Object.keys(params).length === 0) {
      if (tenantUserAccess.AllowedTenants || tenantUserAccess.allowedTenants ||
          tenantUserAccess.TenantUserMap || tenantUserAccess.tenantUserMap) {
        params = tenantUserAccess;
      } else {
        params = {};
      }
    }
    const allowedTenantsValue = params.AllowedTenants || params.allowedTenants;
    if (Array.isArray(allowedTenantsValue)) {
      allowedTenants = allowedTenantsValue.filter(t => t != null && t !== '');
    }
    const tenantUserMapValue = params.TenantUserMap || params.tenantUserMap;
    if (tenantUserMapValue && typeof tenantUserMapValue === 'object' && !Array.isArray(tenantUserMapValue)) {
      tenantUserMap = {};
      for (const [key, value] of Object.entries(tenantUserMapValue)) {
        if (Array.isArray(value)) {
          tenantUserMap[key] = value.filter(u => u != null && u !== '');
        } else if (value && typeof value === 'object' && Array.isArray(value.value)) {
          tenantUserMap[key] = value.value.filter(u => u != null && u !== '');
        } else {
          tenantUserMap[key] = [];
        }
      }
    }
  }

  const flagId = flag.id || flag.Id;

  if (!flagId) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="p-3 bg-red-50 dark:bg-red-950 text-red-600 rounded-lg text-sm">
          Error: Feature flag ID is missing. Please refresh the page.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-slate-700">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{flagId}</h2>
        <span
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
            enabled ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400'
          }`}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 text-red-600 rounded-lg text-sm">{error}</div>
      )}

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Status:</label>
              <button
                onClick={handleToggleEnabled}
                disabled={saving}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 ${
                  enabled
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {enabled ? 'Disable' : 'Enable'}
              </button>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Description:</label>
              {isEditingDescription ? (
                <div className="space-y-3">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveDescription}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-60"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelDescription}
                      disabled={saving}
                      className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-start gap-4">
                  <p className="text-gray-800 dark:text-gray-200 flex-1">{description || 'No description'}</p>
                  <button
                    onClick={() => setIsEditingDescription(true)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shrink-0"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <TenantManager flagId={flagId} allowedTenants={allowedTenants} onUpdate={onUpdate} />
        </div>

        <div>
          <UserManager
            flagId={flagId}
            allowedTenants={allowedTenants}
            tenantUserMap={tenantUserMap}
            onUpdate={onUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default FeatureFlagDetails;
